import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, Users, FileText, Database, GraduationCap, Shield, BookOpen, HelpCircle, CheckSquare, Square } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataManagement = () => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [trainerSearchTerm, setTrainerSearchTerm] = useState('');
  
  // Multi-select states
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [bulkDeleteStudentsOpen, setBulkDeleteStudentsOpen] = useState(false);
  const [bulkDeleteTrainersOpen, setBulkDeleteTrainersOpen] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch students
      const studentsResponse = await axios.get(`${API}/teacher/students`);
      const studentsData = studentsResponse.data?.students || studentsResponse.data;
      setStudents(Array.isArray(studentsData) ? studentsData : []);

      // Fetch trainers
      try {
        const trainersResponse = await axios.get(`${API}/teacher/trainers`);
        const trainersData = trainersResponse.data?.trainers || [];
        setTrainers(Array.isArray(trainersData) ? trainersData : []);
      } catch (trainerError) {
        console.log('Could not fetch trainers:', trainerError);
        setTrainers([]);
      }

      // Fetch submissions
      const submissionsResponse = await axios.get(`${API}/teacher/results-analysis`);
      const submissionsData = submissionsResponse.data?.submissions || [];
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
      
      // Clear selections
      setSelectedStudents([]);
      setSelectedTrainers([]);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(error.response?.data?.detail || t('error') || 'Error loading data');
      setStudents([]);
      setTrainers([]);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Student selection handlers
  const toggleStudentSelection = (studentEmail) => {
    setSelectedStudents(prev => 
      prev.includes(studentEmail) 
        ? prev.filter(e => e !== studentEmail)
        : [...prev, studentEmail]
    );
  };

  const selectAllStudents = () => {
    const allEmails = filteredStudents.map(s => s.email);
    setSelectedStudents(allEmails);
  };

  const deselectAllStudents = () => {
    setSelectedStudents([]);
  };

  // Trainer selection handlers
  const toggleTrainerSelection = (trainerEmail) => {
    setSelectedTrainers(prev => 
      prev.includes(trainerEmail) 
        ? prev.filter(e => e !== trainerEmail)
        : [...prev, trainerEmail]
    );
  };

  const selectAllTrainers = () => {
    // Select only non-main-owner trainers
    const selectableEmails = filteredTrainers
      .filter(t => !t.is_main_owner)
      .map(t => t.email);
    setSelectedTrainers(selectableEmails);
  };

  const deselectAllTrainers = () => {
    setSelectedTrainers([]);
  };

  // Bulk delete handlers
  const handleBulkDeleteStudents = async () => {
    if (selectedStudents.length === 0) return;
    
    setDeletingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const email of selectedStudents) {
      try {
        await axios.delete(`${API}/teacher/students/${encodeURIComponent(email)}`);
        successCount++;
      } catch (error) {
        console.error(`Error deleting student ${email}:`, error);
        failCount++;
      }
    }
    
    setDeletingBulk(false);
    setBulkDeleteStudentsOpen(false);
    
    if (successCount > 0) {
      toast.success(`تم حذف ${successCount} متدرب بنجاح`);
    }
    if (failCount > 0) {
      toast.error(`فشل حذف ${failCount} متدرب`);
    }
    
    fetchData();
  };

  const handleBulkDeleteTrainers = async () => {
    if (selectedTrainers.length === 0) return;
    
    setDeletingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const email of selectedTrainers) {
      try {
        await axios.delete(`${API}/teacher/trainers/${encodeURIComponent(email)}`);
        successCount++;
      } catch (error) {
        console.error(`Error deleting trainer ${email}:`, error);
        failCount++;
      }
    }
    
    setDeletingBulk(false);
    setBulkDeleteTrainersOpen(false);
    
    if (successCount > 0) {
      toast.success(`تم حذف ${successCount} مدرب بنجاح`);
    }
    if (failCount > 0) {
      toast.error(`فشل حذف ${failCount} مدرب`);
    }
    
    fetchData();
  };

  const handleDeleteStudent = async (studentEmail) => {
    try {
      await axios.delete(`${API}/teacher/students/${encodeURIComponent(studentEmail)}`);
      toast.success(t('studentDeleted') || 'تم حذف المتدرب بنجاح');
      fetchData();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteTrainer = async (trainerEmail) => {
    try {
      await axios.delete(`${API}/teacher/trainers/${encodeURIComponent(trainerEmail)}`);
      toast.success('تم حذف المدرب بنجاح');
      fetchData();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    try {
      await axios.delete(`${API}/teacher/submissions/${submissionId}`);
      toast.success(t('submissionDeleted') || 'تم حذف النتيجة بنجاح');
      fetchData();
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // Filter students
  const filteredStudents = Array.isArray(students) ? students.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Filter trainers
  const filteredTrainers = Array.isArray(trainers) ? trainers.filter(trainer =>
    trainer.name?.toLowerCase().includes(trainerSearchTerm.toLowerCase()) ||
    trainer.email?.toLowerCase().includes(trainerSearchTerm.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-lg border-0 bg-gradient-to-r from-red-50 to-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Database className="h-6 w-6" />
            {t('dataManagement') || 'إدارة البيانات'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            {t('dataManagementDescription') || 'حذف المتدربين، المدربين، ونتائج الاختبارات. استخدم بحذر!'}
          </p>
        </CardContent>
      </Card>

      {/* Manage Trainers */}
      <Card className="shadow-md border-l-4 border-l-purple-500">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-purple-700">
              <GraduationCap className="h-5 w-5" />
              إدارة المدربين
              <span className="text-sm font-normal text-gray-500">({trainers.length})</span>
            </div>
            {selectedTrainers.length > 0 && (
              <AlertDialog open={bulkDeleteTrainersOpen} onOpenChange={setBulkDeleteTrainersOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="animate-pulse">
                    <Trash2 className="h-4 w-4 mr-2" />
                    حذف المحدد ({selectedTrainers.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>تأكيد حذف المدربين المحددين</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>سيتم حذف <strong>{selectedTrainers.length}</strong> مدرب نهائياً مع جميع بياناتهم:</p>
                      <ul className="list-disc list-inside text-sm max-h-32 overflow-auto">
                        {selectedTrainers.map(email => (
                          <li key={email}>{email}</li>
                        ))}
                      </ul>
                      <p className="text-red-600 font-semibold">⚠️ هذا الإجراء لا يمكن التراجع عنه!</p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={deletingBulk}>إلغاء</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleBulkDeleteTrainers} 
                      className="bg-red-600 hover:bg-red-700"
                      disabled={deletingBulk}
                    >
                      {deletingBulk ? 'جاري الحذف...' : `حذف ${selectedTrainers.length} مدرب`}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <Input
              placeholder="بحث عن مدرب..."
              value={trainerSearchTerm}
              onChange={(e) => setTrainerSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <Button variant="outline" size="sm" onClick={selectAllTrainers}>
              <CheckSquare className="h-4 w-4 mr-1" />
              تحديد الكل
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAllTrainers}>
              <Square className="h-4 w-4 mr-1" />
              إلغاء التحديد
            </Button>
          </div>
          
          <div className="space-y-3">
            {filteredTrainers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('noData') || 'لا توجد بيانات'}</p>
            ) : (
              filteredTrainers.map((trainer) => (
                <div 
                  key={trainer.id} 
                  className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                    trainer.is_main_owner ? 'bg-yellow-50 border-yellow-200' : ''
                  } ${selectedTrainers.includes(trainer.email) ? 'bg-purple-50 border-purple-300' : ''}`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {!trainer.is_main_owner && (
                      <Checkbox
                        checked={selectedTrainers.includes(trainer.email)}
                        onCheckedChange={() => toggleTrainerSelection(trainer.email)}
                        className="h-5 w-5"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{trainer.name}</p>
                        {trainer.is_main_owner && (
                          <span className="flex items-center gap-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                            <Shield className="h-3 w-3" />
                            المالك الرئيسي
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{trainer.email}</p>
                      <div className="flex gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <HelpCircle className="h-3 w-3" />
                          {trainer.questions_count || 0} سؤال
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {trainer.exams_count || 0} اختبار
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-3 w-3" />
                          {trainer.courses_count || 0} دورة
                        </span>
                      </div>
                    </div>
                  </div>
                  {!trainer.is_main_owner && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('delete') || 'حذف'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>تأكيد حذف المدرب</AlertDialogTitle>
                          <AlertDialogDescription className="space-y-2">
                            <p>سيتم حذف المدرب <strong>{trainer.name}</strong> نهائياً مع جميع بياناته:</p>
                            <ul className="list-disc list-inside text-sm">
                              <li>{trainer.exams_count || 0} اختبار</li>
                              <li>{trainer.questions_count || 0} سؤال</li>
                              <li>{trainer.courses_count || 0} دورة</li>
                            </ul>
                            <p className="text-red-600 font-semibold">⚠️ هذا الإجراء لا يمكن التراجع عنه!</p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel') || 'إلغاء'}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteTrainer(trainer.email)} className="bg-red-600 hover:bg-red-700">
                            حذف المدرب
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manage Students/Trainees */}
      <Card className="shadow-md border-l-4 border-l-cyan-500">
        <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-cyan-700">
              <Users className="h-5 w-5" />
              {t('manageStudents') || 'إدارة المتدربين'}
              <span className="text-sm font-normal text-gray-500">({students.length})</span>
            </div>
            {selectedStudents.length > 0 && (
              <AlertDialog open={bulkDeleteStudentsOpen} onOpenChange={setBulkDeleteStudentsOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="animate-pulse">
                    <Trash2 className="h-4 w-4 mr-2" />
                    حذف المحدد ({selectedStudents.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>تأكيد حذف المتدربين المحددين</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>سيتم حذف <strong>{selectedStudents.length}</strong> متدرب نهائياً مع جميع بياناتهم:</p>
                      <ul className="list-disc list-inside text-sm max-h-32 overflow-auto">
                        {selectedStudents.map(email => (
                          <li key={email}>{email}</li>
                        ))}
                      </ul>
                      <p className="text-red-600 font-semibold">⚠️ سيتم حذف جميع نتائج الاختبارات والفيديوهات المرتبطة!</p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={deletingBulk}>إلغاء</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleBulkDeleteStudents} 
                      className="bg-red-600 hover:bg-red-700"
                      disabled={deletingBulk}
                    >
                      {deletingBulk ? 'جاري الحذف...' : `حذف ${selectedStudents.length} متدرب`}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <Input
              placeholder={t('search') + '...' || 'بحث...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <Button variant="outline" size="sm" onClick={selectAllStudents}>
              <CheckSquare className="h-4 w-4 mr-1" />
              تحديد الكل
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAllStudents}>
              <Square className="h-4 w-4 mr-1" />
              إلغاء التحديد
            </Button>
          </div>
          
          <div className="space-y-3">
            {filteredStudents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('noData') || 'لا توجد بيانات'}</p>
            ) : (
              filteredStudents.map((student) => (
                <div 
                  key={student.id} 
                  className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                    selectedStudents.includes(student.email) ? 'bg-cyan-50 border-cyan-300' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedStudents.includes(student.email)}
                      onCheckedChange={() => toggleStudentSelection(student.email)}
                      className="h-5 w-5"
                    />
                    <div>
                      <p className="font-semibold">{student.name}</p>
                      <p className="text-sm text-gray-600">{student.email}</p>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('delete') || 'حذف'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('confirmDelete') || 'تأكيد الحذف'}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('deleteStudentWarning') || 'سيتم حذف هذا المتدرب نهائياً مع جميع نتائج اختباراته وفيديوهاته. هذا الإجراء لا يمكن التراجع عنه.'}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel') || 'إلغاء'}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteStudent(student.email)} className="bg-red-600 hover:bg-red-700">
                          {t('delete') || 'حذف'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manage Exam Results */}
      <Card className="shadow-md border-l-4 border-l-orange-500">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <FileText className="h-5 w-5" />
            {t('manageResults') || 'إدارة نتائج الاختبارات'}
            <span className="text-sm font-normal text-gray-500">({submissions.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            {submissions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('noData') || 'لا توجد بيانات'}</p>
            ) : (
              submissions.slice(0, 20).map((submission) => (
                <div key={submission.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <p className="font-semibold">{submission.student_name}</p>
                    <p className="text-sm text-gray-600">{submission.student_email}</p>
                    <p className="text-sm text-gray-500">
                      {t('exam') || 'الاختبار'}: {submission.exam_title} | {t('score') || 'الدرجة'}: {submission.score}%
                    </p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('delete') || 'حذف'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('confirmDelete') || 'تأكيد الحذف'}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('deleteSubmissionWarning') || 'سيتم حذف هذه النتيجة نهائياً مع الفيديوهات المرتبطة بها. هذا الإجراء لا يمكن التراجع عنه.'}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel') || 'إلغاء'}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteSubmission(submission.id)} className="bg-red-600 hover:bg-red-700">
                          {t('delete') || 'حذف'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))
            )}
            {submissions.length > 20 && (
              <p className="text-sm text-gray-500 text-center">{t('showingFirst20') || 'يتم عرض أول 20 نتيجة فقط'}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataManagement;
